/** The accounts of the Accounts page in the client cache (rule U-36). */

import { useMemo, useSyncExternalStore } from "react";
import type { Account, RotationCalendar } from "../api";
import { clockTime, readingTime } from "../format";
import { getVersion, merge, note, openScope, patch, readView, removeFromView, setView, stamp, subscribe, wake } from "./store";

export const ACCOUNTS_VIEW = "accounts";
export const COLLECTION = "account";

export interface AccountsExtra {
  can_delete: boolean;
  can_bulk_edit: boolean;
  rotation?: RotationCalendar;
}

export interface AccountListing {
  accounts: Account[];
  can_delete?: boolean;
  can_bulk_edit?: boolean;
  rotation?: RotationCalendar;
  /** The change feed seq of the rows; an older API does not give it. */
  as_of?: number;
}

/** `at` is the time of the request of the kept list, or null with no kept list. */
export interface KeptAccounts {
  accounts: Account[] | undefined;
  extra: AccountsExtra;
  at: number | null;
}

const NONE: AccountsExtra = { can_delete: false, can_bulk_edit: false };

export function keptAccounts(): KeptAccounts {
  const view = readView<Account, AccountsExtra>(ACCOUNTS_VIEW);
  return view ? { accounts: view.rows, extra: view.extra, at: view.at } : { accounts: undefined, extra: NONE, at: null };
}

export function useAccounts(): KeptAccounts {
  const version = useSyncExternalStore(subscribe, getVersion, getVersion);
  return useMemo(() => keptAccounts(), [version]);
}

/** Log one check of the kept list; the server log gets the first check and each check that changed rows. */
export function logRevalidate(rows: number, changed: number, ms: number, first: boolean): void {
  const text = `accounts: ${rows} rows, ${changed} changed, revalidated in ${ms} ms`;
  if (first || changed) note("cache_revalidate", text, { view: ACCOUNTS_VIEW, rows, changed, ms, first });
  else console.info(text);
}

export const accountsExtra = (found: AccountListing): AccountsExtra => ({
  can_delete: Boolean(found.can_delete),
  can_bulk_edit: Boolean(found.can_bulk_edit),
  ...(found.rotation ? { rotation: found.rotation } : {}),
});

/** Keep the answer of GET /api/accounts on an API with no change feed; `asOf` is the time when the request started. */
export function keepAccounts(found: AccountListing, asOf: number): number {
  const changed = merge(COLLECTION, found.accounts, asOf);
  const extra = accountsExtra(found);
  setView(ACCOUNTS_VIEW, COLLECTION, found.accounts.map((account) => account.id), extra, asOf, true);
  return changed;
}

/** A write answer holds the account: change the kept row, but only the fields that the list row has. */
export function keepAccount<T extends Account>(found: T, at: number = stamp()): T {
  const changed = patch<Account>(
    COLLECTION,
    found.id,
    (old) => {
      const next: Record<string, unknown> = { ...old };
      for (const key of Object.keys(old)) if (key in found) next[key] = (found as Record<string, unknown>)[key];
      return next as unknown as Account;
    },
    at,
  );
  if (changed) note("cache_row_write", "cache: an account row kept from a write", { collection: COLLECTION });
  wake("write");
  return found;
}

export function dropAccount(id: string): void {
  removeFromView(ACCOUNTS_VIEW, id);
  wake("write");
}

function ageText(at: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  return minutes < 1 ? "just now" : minutes < 60 ? `${minutes} min ago` : minutes < 1_440 ? `${Math.floor(minutes / 60)} h ago` : `${Math.floor(minutes / 1_440)} d ago`;
}

/** "Updated 4 min ago · checking…" for a kept list while the new answer comes. */
export function keptText(at: number, now: number = Date.now()): string {
  return `Updated ${ageText(at, now)} · checking…`;
}

/** The age of a kept list when the check failed, with its clock time, and its day when that is not today. */
export function failedText(at: number, timeZone?: string, now: number = Date.now()): string {
  const day = (ms: number) => new Date(ms).toLocaleDateString("en-CA", { timeZone });
  const when = day(at) === day(now) ? clockTime(new Date(at), timeZone) : readingTime(new Date(at).toISOString(), timeZone);
  return `Updated ${ageText(at, now)} (${when}) · not checked`;
}

/** Log the open of the Accounts page: a hit with its rows and age, or a miss; `ms` is the wait for the disk. */
export function logAccountsOpen(kept: KeptAccounts, ms: number, now: number = Date.now()): void {
  if (kept.at === null || !kept.accounts) return note("cache_miss", cacheLine(kept, now), { view: ACCOUNTS_VIEW, ms });
  note("cache_hit", cacheLine(kept, now), { view: ACCOUNTS_VIEW, rows: kept.accounts.length, age_s: Math.round((now - kept.at) / 1_000), ms });
}

/** Log the open of the Accounts page after the scope of the user has its rows from the disk. */
export async function logAccountsLoad(slug: string, uid: string): Promise<void> {
  const started = performance.now();
  await openScope(slug, uid);
  logAccountsOpen(keptAccounts(), Math.round(performance.now() - started));
}

/** The log line when the Accounts page opens: a hit with its rows and age, or a miss. */
export function cacheLine(kept: KeptAccounts, now: number = Date.now()): string {
  if (kept.at === null || !kept.accounts) return "cache: accounts miss";
  return `cache: accounts hit, ${kept.accounts.length} rows, ${Math.round((now - kept.at) / 1_000)} s old`;
}
